<script context="module" lang="ts">
    export type GridAlgorithmParams = {
        mode: string,
        gridSet: string,
        mapId: number,
        cells: number[],
        zoneIds?: number[]|null,
        attribute: string,
        vehicleCount?: number,
        maxRadius?: number,
        maxLoad?: number,
        ignoreMapping?: boolean,
        timeout?: number
    };
</script>

<script lang="ts">
    import { closeModal } from 'svelte-modals';
    import { _ } from 'svelte-i18n';
    import { get } from 'svelte/store';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import ButtonBack from '../../shared/ButtonBack.svelte';
    import { api } from '../../../stores/api';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import Select from '../../shared/Select.svelte';
    import ExtendedSelect from '../../shared/ExtendedSelect.svelte';
    import TelematicsSliderSingle from '../TelematicsSlider/TelematicsSliderSingle.svelte';
    import RadioButton from '../../shared/RadioButton.svelte';
    import { onMount } from 'svelte';
    import { z } from 'zod';
    import Checkbox from '../../shared/Checkbox/Checkbox.svelte';

    export let isOpen: boolean = true;
    export let error: string = '';

    export let onProceed: (params: GridAlgorithmParams) => Promise<boolean|string> = async function(params: GridAlgorithmParams) { return true };
    export let onCancel: () => void = () => {};

    export let gridSet: string;
    export let mapId: number;
    export let cells: number[];
    export let zoneIds: number[]|null = null;
    export let attribute: string = "attributes->'rg'->'kv'->'volume'->>'value'";
    export let vehicleCount: number = 10;
    export let maxRadius: number = 100;
    export let maxLoad: number|null = null;
    export let ignoreMapping: boolean = true;
    export let timeout: number = 30;

    let busy: boolean = false;
    let totalVolume: number = 0;
    let expanded: boolean = false;
    let maxNumberOfZones: number = 100;

    let availableAttributes = [
        {id: "attributes->'rg'->'kv'->'volume'->>'value'", label: 'volume'}
    ];
    if (attribute.match(/^attributes->'[a-f0-9]+'->'volume'->>'value'$/)) {
        availableAttributes = [{id: attribute, label: 'volume'}];
    }

    async function proceed() {
        if (isFormValid()) {
            busy = true;
            let proceed = await onProceed({
                gridSet,
                mapId,
                cells,
                attribute,
                vehicleCount,
                maxRadius,
                maxLoad,
                ignoreMapping,
                timeout,
                zoneIds
            })
            if (proceed === true) {
                closeModal();
            } else if (typeof(proceed) == 'string') {
                error = proceed;
                busy = false;
            }
        }
    }

    function isFormValid() {
        return true;
    }

    onMount(() => {
        busy = true;
        $api.getData(
            'post',
            '/micro_grid/get_total_volume',
            {
                set: gridSet,
                cells: cells,
                zoneIds: zoneIds,
                attribute: attribute
            },
            z.any(),
            z.any()
        ).then((response: any) => {
            if (response.cells) {
                cells = response.cells;
            }
            vehicleCount = Math.min(vehicleCount, cells.length);
            maxNumberOfZones = Math.min(maxNumberOfZones, cells.length);
            if (typeof(response.total) == 'number') {
                totalVolume = response.total;
                busy = false;
            }
        });
    });

    $: {
        let newMaxLoad = 115 * (totalVolume / vehicleCount) / 100;
        if (1 || !maxLoad || newMaxLoad > maxLoad) {
            maxLoad = Math.round(newMaxLoad);
        }
    }


</script>

{#if isOpen}
    <div role="dialog" class="modal">
        <div class="contents">
            {#if busy}
                <div style="display: flex; width: 100%; height: 100%; align-items: center; justify-contents: center;">
                    <Spinner/>
                </div>
            {:else if totalVolume > 0 && (maxLoad || 0) > 0}
                {#if error != ''}
                    <div class="error">{@html error}</div>
                {/if}

                <h3 style="text-align: center; margin-bottom: 5px">{$_('grid-algorithm-set-parameters')}</h3>

                <div class="grid-algorithm-param">
                    <span>{$_('grid-algorithm-param-vehiclecount')}</span>
                    <div class="slider">
                        <TelematicsSliderSingle props={{min: 1, max: maxNumberOfZones, currentValue: vehicleCount, unit: '', roundValues: true, sliderWidth: 250}} onChange={(val) => { vehicleCount = Math.round(val) }}/>
                    </div>
                </div>
                <div class="grid-algorithm-param">
                    <span>{$_('grid-algorithm-param-maxradius')}</span>
                    <div class="control">
                        <ExtendedSelect bind:value={maxRadius} min={2} max={100} options={[2, 5, 10, 20, 50, 100]} step={5} inputWidth={70} showBorder={true}/>
                    </div>
                </div>
                {#if maxLoad !== null}
                    <div class="grid-algorithm-param">
                        <span>{$_('grid-algorithm-param-maxload')}</span>
                        <div class="control">
                            <ExtendedSelect bind:value={maxLoad} options={[]} percentStep={10} inputWidth={70} parseValue={(val) => Math.round(val)}/>
                            <span style="display: inline-block; margin-left: 15px">(suma: {totalVolume})</span>
                        </div>
                    </div>
                {/if}
                <div class="grid-algorithm-param">
                    <span>{$_('grid-algorithm-param-attribute')}</span>
                    <div class="control">
                        <Select items={availableAttributes} selected={attribute} withBorder={true} onChange={(val) => {
                            attribute = val;
                            return true;
                        }}/>
                    </div>
                </div>
                {#if expanded}
                    <div class="expand"><span on:click={() => { expanded = false; }}>{$_('less')}</span></div>
                    <div class="control">
                        <Checkbox label={$_('grid-algorithm-param-ignoremapping')} bind:checked={ignoreMapping}/>
                    </div>
                    <div class="grid-algorithm-param">
                        <span>{$_('grid-algorithm-param-timeout')}</span>
                        <div class="control">
                            <ExtendedSelect bind:value={timeout} options={[15, 30, 60, 90, 120, 180, 300]} step={30} inputWidth={50}/>
                        </div>
                    </div>
                {:else}
                    <div class="expand"><span on:click={() => { expanded = true; }}>{$_('more')}</span></div>
                {/if}

                <div class="actions">
                    <ButtonBack
                        text={$_('Cancel')}
                        on:click={() => {
                            closeModal();
                            onCancel();
                        }}
                    />
                    <ButtonMain
                        text={$_('OK')}
                        disabled={false}
                        on:click={proceed}
                    />
                </div>
            {:else}
                <div class="error">{$_(totalVolume > 0 ? 'grid-algorithm-selection-load-too-small' : 'grid-algorithm-empty-selection')}</div>
                <ButtonMain
                    text={$_('OK')}
                    disabled={false}
                    on:click={() => {
                        closeModal();
                        onCancel();
                    }}
                />
            {/if}
        </div>
    </div>
{/if}

<style>
    .modal {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1002;

        /* allow click-through to backdrop */
        pointer-events: none;
    }

    .contents {
        width: 650px;
        border-radius: 6px;
        background: white;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        pointer-events: auto;
        font-size: 14px;
        padding: 30px;
        border: 3px solid var(--automapa-blue);
        box-shadow:
            0 0 0 10px #1c5dc722,
            0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .grid-algorithm-param {
        margin-top: 10px;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
    }
    .grid-algorithm-param>span {
        width: 33%;
        text-align: right;
    }

    .slider {
        margin: 5px 0;
    }

    .control {
        display: flex;
        align-items: center;
    }

    .expand {
        display: flex;
        justify-content: center;
        border-bottom: 1px dashed #555;
        margin-bottom: 15px;
    }
    .expand span {
        display: inline-block;
        position: relative;
        top: 9px;
        background-color: #ffffff;
        padding: 1px 10px;
        cursor: pointer;
    }

    .actions {
        margin-top: 32px;
        display: flex;
        justify-content: center;
        column-gap: 10px;
    }

    .error {
        color: var(--automapa-red);
        margin-bottom: 10px;
    }

</style>
